import type ptTranslations from '@/locales/pt.json';
import type { MetricsMenuChannel } from './metricsMenuPaths';

export type MetricsMenuLabelKey = keyof typeof ptTranslations.metricsMenu;

export type MetricsMenuNode = {
  key: string;
  label: MetricsMenuLabelKey;
  children?: MetricsMenuNode[];
  defaultOpen?: boolean;
};

const COMMON_COMMENTS_ANALYSIS: MetricsMenuNode = {
  key: 'commentsAnalysis',
  label: 'commentsAnalysis',
  children: [
    { key: 'commentsSentiment', label: 'commentsSentiment' },
    { key: 'wordcloud', label: 'wordcloud' },
  ],
};

const COMMON_STANDARD_POSTS_ANALYSIS: MetricsMenuNode = {
  key: 'postsAnalysis',
  label: 'postsAnalysis',
  children: [
    { key: 'fanbaseEvolution', label: 'fanbaseEvolution' },
    { key: 'useActivityByDayHour', label: 'useActivityByDayHour' },
    { key: 'postsVsInteractions', label: 'postsVsInteractions' },
    { key: 'volumeOfInteractions', label: 'volumeOfInteractions' },
  ],
};

export const channelMenuItems: Record<MetricsMenuChannel, MetricsMenuNode[]> = {
  facebook: [
    { key: 'overview', label: 'overview' },
    { key: 'topEngagedUsers', label: 'topEngagedUsers' },
    {
      key: 'facebookInsights',
      label: 'facebookInsights',
      children: [{ key: 'facebookPageLikesCampaign', label: 'facebookPageLikesCampaign' }],
    },
    {
      key: 'postsAnalysis',
      label: 'postsAnalysis',
      children: [
        { key: 'fanbaseEvolution', label: 'fanbaseEvolution' },
        { key: 'activePosts', label: 'activePosts' },
        { key: 'paidImpressions', label: 'paidImpressions' },
        { key: 'organicImpressions', label: 'organicImpressions' },
        { key: 'interactionRate', label: 'interactionRate' },
        { key: 'interactionsVolume', label: 'interactionsVolume' },
        { key: 'useActivityByDayHour', label: 'useActivityByDayHour' },
        { key: 'organicVsPaid', label: 'organicVsPaid' },
        { key: 'postsVsInteractions', label: 'postsVsInteractions' },
        { key: 'reachVsEngagement', label: 'reachVsEngagement' },
      ],
    },
    COMMON_COMMENTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
    { key: 'inbox', label: 'inbox' },
    { key: 'reviews', label: 'reviews' },
    { key: 'visitorPosts', label: 'visitorPosts' },
    { key: 'compareFanpages', label: 'compareFanpages' },
  ],
  instagram: [
    { key: 'overview', label: 'overview' },
    {
      key: 'adsAnalysis',
      label: 'adsAnalysis',
      children: [
        { key: 'adsSet', label: 'adsSet' },
        { key: 'ads', label: 'ads' },
      ],
    },
    { key: 'topEngagedUsers', label: 'topEngagedUsers' },
    {
      key: 'postsAnalysis',
      label: 'postsAnalysis',
      children: [
        { key: 'fanbaseEvolution', label: 'fanbaseEvolution' },
        { key: 'postsVsInteractions', label: 'postsVsInteractions' },
        { key: 'volumeOfInteractions', label: 'volumeOfInteractions' },
        { key: 'impressionsOverTime', label: 'impressionsOverTime' },
        { key: 'useActivityByDayHour', label: 'useActivityByDayHour' },
        { key: 'reachOverTime', label: 'reachOverTime' },
        { key: 'viewsOverTime', label: 'viewsOverTime' },
        { key: 'interactionsInStories', label: 'interactionsInStories' },
      ],
    },
    COMMON_COMMENTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'stories', label: 'stories' },
    { key: 'tagged', label: 'tagged' },
    { key: 'mentions', label: 'mentions' },
    { key: 'externalComments', label: 'externalComments' },
    { key: 'commentsInMentions', label: 'commentsInMentions' },
    { key: 'allComments', label: 'allComments' },
  ],
  tiktok: [
    { key: 'overview', label: 'overview' },
    { key: 'audience', label: 'audience' },
    {
      key: 'postsAnalysis',
      label: 'postsAnalysis',
      children: [
        { key: 'postsVsInteractions', label: 'postsVsInteractions' },
        { key: 'volumeOfInteractions', label: 'volumeOfInteractions' },
      ],
    },
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
    { key: 'mentions', label: 'mentions' },
    { key: 'directMessages', label: 'directMessages' },
  ],
  twitter: [
    { key: 'overview', label: 'overview' },
    COMMON_STANDARD_POSTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
    { key: 'directMessages', label: 'directMessages' },
    { key: 'mentions', label: 'mentions' },
  ],
  youtube: [
    { key: 'overview', label: 'overview' },
    {
      key: 'postsAnalysis',
      label: 'postsAnalysis',
      children: [
        { key: 'fanbaseEvolutionSubscribers', label: 'fanbaseEvolutionSubscribers' },
        { key: 'useActivityByDayHour', label: 'useActivityByDayHour' },
        { key: 'postsVsInteractions', label: 'postsVsInteractions' },
        { key: 'volumeOfInteractions', label: 'volumeOfInteractions' },
      ],
    },
    COMMON_COMMENTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
  ],
  linkedin: [
    { key: 'overview', label: 'overview' },
    COMMON_STANDARD_POSTS_ANALYSIS,
    COMMON_COMMENTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
  ],
  threads: [
    { key: 'overview', label: 'overview' },
    COMMON_STANDARD_POSTS_ANALYSIS,
    COMMON_COMMENTS_ANALYSIS,
    { key: 'posts', label: 'posts' },
    { key: 'allComments', label: 'allComments' },
  ],
  googleplay: [
    { key: 'overview', label: 'overview' },
    {
      key: 'reviewsAnalysis',
      label: 'reviewsAnalysis',
      children: [
        { key: 'reviewsSentiment', label: 'reviewsSentiment' },
      ],
    },
  ],
  allpages: [{ key: 'overview', label: 'overview' }],
  googlemybusiness: [
    { key: 'overview', label: 'overview' },
    {
      key: 'reviewsAnalysis',
      label: 'reviewsAnalysis',
      children: [
        { key: 'reviewsSentiment', label: 'reviewsSentiment' },
      ],
    },
  ],
};